'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

type LogoVariant = 'auto' | 'light' | 'dark' | 'monochrome' | 'white';

const ICON_COLORS: Record<Exclude<LogoVariant, 'auto'>, string> = {
  light: '#2D6A4F',
  dark: '#52B788',
  monochrome: '#0F1614',
  white: '#FFFFFF',
};

const TEXT_COLORS: Record<Exclude<LogoVariant, 'auto'>, string> = {
  light: '#0D2A22',
  dark: '#E8EEE9',
  monochrome: '#0F1614',
  white: '#FFFFFF',
};

function useThemeVariant(variant: LogoVariant): Exclude<LogoVariant, 'auto'> {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (variant !== 'auto') {
    return variant;
  }

  if (!mounted) {
    return 'light';
  }

  return resolvedTheme === 'dark' ? 'dark' : 'light';
}

interface LogoProps {
  variant?: LogoVariant;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Logo component with theme-aware variants (inline SVG)
 *
 * Variants:
 * - 'auto': Automatically selects based on current theme
 * - 'light': Green icon (#2D6A4F) + dark text (#0D2A22) - for light backgrounds
 * - 'dark': Green icon (#52B788) + light text (#E8EEE9) - for dark backgrounds
 * - 'monochrome': Single color (#0F1614) - for special use cases
 * - 'white': White icon + text (#FFFFFF) - for colored backgrounds (e.g., primary buttons/headers)
 */
export function Logo({
  variant = 'auto',
  width = 187,
  height = 46,
  className = '',
}: LogoProps): React.ReactElement {
  const resolvedVariant = useThemeVariant(variant);
  const iconColor = ICON_COLORS[resolvedVariant];
  const textColor = TEXT_COLORS[resolvedVariant];

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 187 46"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="BiteClub Logo"
    >
      <path
        fill={iconColor}
        fillRule="evenodd"
        d="M0 13.528c0-2.68 1.099-5.254 3.256-6.832C7.026 3.938 13.583 0 20.28 0c6.676 0 13.62 3.912 17.684 6.668 2.34 1.587 3.571 4.329 3.571 7.164-2.607-.181-5.31-.184-8.36-.034-2.24.132-4.5.362-6.775.688-.163.023-.407.064-.696.112h-.002c-1.145.193-3.004.506-3.432.318-2.728-4.932-15.022-2.14-18.155 4.545-1.696 3.62.986 6.188 3.763 6.642 3.78.617 7.072-.279 10.941-2.738.136-.124.293-.243.453-.363.176-.133.355-.269.514-.418 2.015-1.892 2.332-2.715 3.165-5.02.988-.317 3.245-.555 4.276-.644 5.029-.436 9.707.643 14.298 1.862C41.21 38.85 33.23 45.518 20.28 45.518c-8.777 0-14.842-3.064-17.925-11.289 4.59.289 9.177.14 13.423-.442.118-.017.29-.05.493-.09.823-.158 2.154-.415 2.494-.034.098.11.175.237.26.376.127.209.271.445.524.688.822.8 1.304.986 2.248 1.352l.017.006c.04.015.08.03.12.047 2.807.703 4.877.07 7.073-.868a80.718 80.718 0 0 1 2.767-1.102c1.454-.558 2.91-1.118 4.276-1.791 1.188-.592.363-2.205-.438-3.24-.353-.458-2.081.346-2.77.666-.101.047-.18.084-.228.104-1.822.78-3.653 1.548-5.49 2.304-.236.098-.477.216-.721.335-.438.213-.886.431-1.335.552a1.167 1.167 0 0 1-.798-.066.946.946 0 0 1-.441-.481c-.115-.304-.022-.603.124-.809.394-.557 4.428-2.187 6.66-3.088.517-.209.937-.379 1.193-.487.17-.072.354-.142.542-.213.595-.226 1.238-.47 1.643-.857a.68.68 0 0 0 .205-.666c-.08-.393-.568-1.246-.937-1.508-.144-.102-.197-.113-.351-.108-.63.023-4.478 1.804-6.571 2.772l-.006.003-.004.002-.004.002c-.531.245-.948.438-1.167.535-.133.059-.28.133-.433.212-.4.204-.851.434-1.245.475-.2.02-.39-.03-.595-.135-.559-.695.255-1.384.715-1.598a225.572 225.572 0 0 1 4.111-1.837c.926-.406 1.85-.812 2.77-1.226l.186-.081c.597-.26 1.387-.605 1.442-1.29.021-.252-.074-.531-.262-.77-.53-.676-1.069-.69-1.653-.474-.727.27-1.467.621-2.177.959l-.62.293c-.302.14-.604.279-.908.417-2.792 1.277-5.603 2.562-7.068 5.302-.144.27-.276.57-.41.876-.253.577-.513 1.17-.88 1.604-.285.252-1.769.4-2.583.482l-.35.036c-4.41.497-9.13-.777-13.912-2.068l-.314-.084C.326 25.925 0 22.054 0 17.532v-4.004Z"
        clipRule="evenodd"
      />
      <path
        fill={textColor}
        d="M105.729 18.145c1.387 0 2.614.223 3.681.671 1.066.427 1.962 1.035 2.688 1.824a7.956 7.956 0 0 1 1.696 2.752c.384 1.024.576 2.144.576 3.36 0 .341-.022.683-.064 1.024-.022.32-.075.598-.16.832H101.91c.057.447.166.864.331 1.248.32.747.812 1.334 1.473 1.76.661.405 1.461.608 2.399.608.854 0 1.58-.17 2.177-.512a3.652 3.652 0 0 0 1.439-1.408l3.84 1.824c-.341.853-.885 1.6-1.631 2.24-.726.64-1.59 1.141-2.592 1.504-1.003.341-2.102.512-3.296.512-1.856 0-3.467-.405-4.832-1.216a8.628 8.628 0 0 1-3.168-3.328c-.747-1.387-1.12-2.922-1.12-4.608 0-1.749.384-3.307 1.152-4.672a8.855 8.855 0 0 1 3.168-3.232c1.323-.79 2.816-1.183 4.479-1.183Zm22.637-6.369c1.686 0 3.19.288 4.513.864 1.344.576 2.474 1.333 3.392 2.272.917.917 1.579 1.92 1.984 3.008l-3.393 1.6a6.776 6.776 0 0 0-2.464-3.104c-1.109-.81-2.453-1.216-4.032-1.216-1.578 0-2.975.374-4.191 1.12-1.216.747-2.166 1.781-2.849 3.104-.661 1.323-.991 2.87-.991 4.64 0 1.77.33 3.328.991 4.672.683 1.323 1.633 2.357 2.849 3.104 1.216.725 2.613 1.088 4.191 1.088 1.579 0 2.923-.395 4.032-1.184a6.777 6.777 0 0 0 2.464-3.104l3.393 1.6c-.405 1.067-1.067 2.07-1.984 3.008-.918.939-2.048 1.696-3.392 2.272-1.323.575-2.827.864-4.513.864-1.685 0-3.253-.31-4.704-.928a11.686 11.686 0 0 1-3.743-2.56c-1.045-1.109-1.867-2.41-2.464-3.904-.597-1.493-.896-3.136-.896-4.928 0-1.77.288-3.403.864-4.896.597-1.515 1.429-2.816 2.496-3.904a11.042 11.042 0 0 1 3.743-2.56c1.429-.619 2.998-.928 4.704-.928Zm25.488 17.376c.001.79.16 1.472.48 2.048.32.576.769 1.024 1.345 1.344.576.32 1.247.48 2.015.48s1.44-.16 2.016-.48a3.396 3.396 0 0 0 1.345-1.344c.32-.576.479-1.258.479-2.048V18.69h3.584V29.28c0 1.408-.309 2.656-.928 3.744a6.483 6.483 0 0 1-2.591 2.495c-1.11.577-2.422.865-3.937.865-1.472 0-2.774-.288-3.904-.864a6.492 6.492 0 0 1-2.592-2.496c-.619-1.088-.928-2.336-.928-3.744V18.69h3.616v10.463Zm18.461-8.47a6.756 6.756 0 0 1 1.984-1.546c1.066-.555 2.283-.832 3.648-.832 1.643 0 3.115.395 4.416 1.184a8.37 8.37 0 0 1 3.104 3.232c.768 1.365 1.152 2.902 1.152 4.608 0 1.707-.384 3.242-1.152 4.608a8.693 8.693 0 0 1-3.071 3.264c-1.302.79-2.785 1.184-4.449 1.184-1.344 0-2.57-.278-3.679-.832a5.986 5.986 0 0 1-2.113-1.767V36h-3.456V11.776h3.616v8.906Zm-81.59-2.154h4.095v4.16h-4.095v6.688c0 .597.107 1.098.32 1.504.213.384.543.672.991.864.448.192 1.003.288 1.664.288.15 0 .32-.01.513-.032l.607-.064V36c-.32.043-.682.085-1.088.128-.405.043-.79.064-1.152.064-2.112 0-3.754-.565-4.928-1.696-1.152-1.152-1.727-2.752-1.727-4.8v-7.008H82.98v-4.16h.16c.895 0 1.578-.224 2.047-.672.491-.448.737-1.12.737-2.016v-1.28h4.8v3.968Zm54.356 12.576c0 1.3.704 1.952 2.111 1.952h.193v3.136h-.704c-1.75 0-3.061-.395-3.936-1.184-.853-.79-1.28-1.995-1.28-3.616V11.776h3.616v19.328ZM64.193 12.16c1.643 0 3.05.278 4.224.832 1.173.555 2.07 1.354 2.689 2.4.618 1.024.927 2.272.927 3.744 0 1.045-.288 2.048-.864 3.008-.215.35-.481.679-.8.987.326.224.615.46.864.709.683.683 1.153 1.43 1.409 2.24.256.79.383 1.622.383 2.496 0 2.347-.778 4.17-2.336 5.472C69.132 35.349 66.968 36 64.194 36h-10.08V12.16h10.08ZM80.655 36h-4.8V18.528h4.8V36Zm96.941-14.336c-1.003 0-1.91.246-2.721.736a5.378 5.378 0 0 0-1.888 2.016c-.448.853-.672 1.824-.672 2.912 0 1.088.224 2.07.672 2.945a5.379 5.379 0 0 0 1.888 2.015c.811.49 1.718.736 2.721.736 1.024 0 1.93-.245 2.719-.736a5.127 5.127 0 0 0 1.856-2.015c.469-.875.704-1.857.704-2.945 0-1.088-.235-2.059-.704-2.912a5.127 5.127 0 0 0-1.856-2.016c-.789-.49-1.695-.736-2.719-.736ZM59.073 31.68h5.696c1.003 0 1.803-.288 2.4-.864.597-.576.896-1.323.896-2.24 0-.939-.299-1.696-.896-2.272-.597-.576-1.397-.863-2.4-.864h-5.696v6.24Zm46.656-9.696c-.831 0-1.546.203-2.143.608-.597.405-1.046 1.003-1.345 1.792a5.22 5.22 0 0 0-.206.704h7.247a3.54 3.54 0 0 0-.16-.768c-.235-.725-.651-1.29-1.248-1.696-.576-.427-1.291-.64-2.145-.64Zm-46.656-.864h5.472c.768 0 1.376-.213 1.824-.64.47-.448.704-1.035.704-1.76 0-.726-.235-1.301-.704-1.728-.448-.448-1.056-.672-1.824-.672h-5.472v4.8Zm21.582-4.16h-4.8v-4.8h4.8v4.8Z"
      />
    </svg>
  );
}

interface LogoIconProps {
  variant?: LogoVariant;
  size?: number;
  className?: string;
}

// Icon viewBox dimensions for aspect ratio calculation
const ICON_VIEWBOX_WIDTH = 22;
const ICON_VIEWBOX_HEIGHT = 24;

/**
 * Icon-only version of the logo (just the "B" mark)
 * For use in compact spaces like sidebar headers
 *
 * The size prop sets the height, width is calculated to maintain aspect ratio
 */
export function LogoIcon({
  variant = 'auto',
  size = 32,
  className = '',
}: LogoIconProps): React.ReactElement {
  const resolvedVariant = useThemeVariant(variant);
  const width = Math.round(size * (ICON_VIEWBOX_WIDTH / ICON_VIEWBOX_HEIGHT));

  return (
    <svg
      width={width}
      height={size}
      viewBox={`0 0 ${ICON_VIEWBOX_WIDTH} ${ICON_VIEWBOX_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="BiteClub Icon"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 7.133C0 5.72.582 4.363 1.725 3.53 3.72 2.076 7.195 0 10.742 0c3.536 0 7.214 2.063 9.366 3.516C21.348 4.353 22 5.798 22 7.293c-1.38-.095-2.813-.097-4.428-.018q-1.78.105-3.589.363c-.086.012-.215.034-.368.059h-.001c-.607.102-1.591.267-1.818.168-1.445-2.6-7.957-1.129-9.616 2.396-.899 1.909.522 3.263 1.993 3.502 2.002.325 3.746-.147 5.795-1.444.072-.065.155-.128.24-.19.093-.071.188-.143.272-.221 1.067-.998 1.235-1.432 1.676-2.647.524-.167 1.72-.293 2.265-.34 2.664-.23 5.142.34 7.574.982C21.828 20.484 17.6 24 10.742 24c-4.65 0-7.862-1.616-9.495-5.952 2.432.152 4.861.074 7.11-.233.063-.01.154-.027.261-.048.436-.083 1.141-.219 1.321-.018.052.058.093.125.138.199.067.11.144.234.278.362.435.422.69.52 1.19.713l.01.003.063.025c1.487.37 2.583.037 3.746-.458q.728-.304 1.466-.58c.77-.295 1.541-.59 2.265-.945.629-.312.192-1.163-.232-1.708-.187-.242-1.102.182-1.468.35q-.081.04-.12.056-1.448.617-2.908 1.214c-.125.052-.253.114-.382.177-.232.113-.47.227-.707.291a.62.62 0 0 1-.423-.035.5.5 0 0 1-.233-.253.46.46 0 0 1 .065-.427c.209-.293 2.346-1.153 3.528-1.628.274-.11.496-.2.632-.257q.136-.057.287-.112c.315-.12.656-.248.87-.452a.36.36 0 0 0 .109-.351c-.043-.207-.301-.657-.497-.795-.076-.054-.104-.06-.186-.057-.333.012-2.371.951-3.48 1.462l-.003.001-.002.001-.002.001c-.282.13-.502.231-.618.282q-.107.048-.23.112c-.212.108-.45.229-.66.25a.56.56 0 0 1-.315-.07c-.296-.367.136-.73.38-.843q1.083-.495 2.177-.969.736-.32 1.467-.646l.098-.043c.317-.137.735-.319.764-.68a.6.6 0 0 0-.139-.406c-.28-.357-.566-.364-.875-.25-.385.142-.777.327-1.153.506l-.329.154-.48.22c-1.48.673-2.968 1.35-3.744 2.796-.076.142-.146.3-.217.461-.134.305-.272.617-.467.846-.15.133-.937.211-1.368.254l-.185.02c-2.336.261-4.836-.41-7.369-1.091l-.166-.044C.173 13.669 0 11.628 0 9.244z"
        fill={ICON_COLORS[resolvedVariant]}
      />
    </svg>
  );
}
